import React, { useState } from "react";
import { IS_VATAR, HOME_PATH } from "../vatar/env";
import { Crown, UserMinus } from "lucide-react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { QRCodePopup } from "../Components/QrCodeGenerator";
import BrandedQRCode from "../Components/BrandedQRCode";
import CategoryManager from "../Components/CategoryManager";
import RoomSettingsLabel from "../Components/RoomSettingsLabel";
import { getAiReviewCost } from "../Components/Utils/aiReviewPricing";
import ChatContainer from "../Components/Chat/ChatContainer";
import RoomUpgrade from "../Components/RoomUpgrade";
import useCoinStore from "../stores/coinStore";
import CoinStore from "../Components/CoinStore";
import useRoomData from "../hooks/useRoomData";
import analytics from "../Services/analyticsService";
import { formatTimeDisplay } from "../Components/Utils/roomHelpers";
import GameRulesModal from "../Components/Modals/GameRulesModal";
import { CLASSIC_CATEGORIES } from "../Components/Utils/categoryRules";
import ConfirmationModal from "../Components/Modals/ConfirmationModal";
import { LobbyMascot, useRoomMascots } from "../Components/Mascot";
import FinalGameResults from "../Components/FinalGameResults";
import HostingLimitModal from "../Components/HostingLimitModal";
import { ROOM_LIMIT_COST } from "../Services/roomLimits";
import { REPLAY_COPY, waitingForPlayers } from "../utils/hostingCopy";

// Same options as the mobile app's TimerControl.tsx. 30s is about the
// fastest a phone player can fill 4 categories; past 90s fast players wait.
const TIMER_OPTIONS = [
  { seconds: 30, label: "Fast" },
  { seconds: 45, label: "Normal" },
  { seconds: 60, label: "Relaxed" },
  { seconds: 90, label: "Long" },
];

const SettingToggle = ({ title, description, checked, onClick, disabled, badge }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={onClick}
    disabled={disabled}
    className="w-full flex items-start gap-3 text-left py-3 px-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
  >
    <div className="flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-gray-800">{title}</span>
        {badge}
      </div>
      <p className="text-xs text-gray-500 mt-1">{description}</p>
    </div>
    <span
      className={`relative mt-0.5 inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
        checked ? "bg-red-600" : "bg-gray-300"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-5" : ""
        }`}
      />
    </span>
  </button>
);

const CreateRoom = () => {
  const {
    roomData,
    loading,
    gameStatus,
    roomDocId,
    roomMaxPlayers,
    customTimeLimit,
    hasLetterPicker,
    customCategories,
    isHost,
    setCustomTimeLimit,
    // setHasCustomTimeLimit,
    setHasLetterPicker,
    updateCustomTimeLimitValue,
    handleRoomUpgrade,
    leaveRoom,
    deleteRoom,
    removePlayer,
    transferHost,
    handleCategoriesChange,
    startGame,
    startingGame,
    hostingLimit,
    setHostingLimit,
    endGame,
  } = useRoomData();

  // const navigate = useNavigate();
  const [roomUrl, setRoomUrl] = useState("");
  const [roomCode, setRoomCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [settings, showSettings] = useState(false);
  const [showQRPopup, setShowQRPopup] = useState(false);
  const [enableSpecialRounds, setEnableSpecialRounds] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });
  const [, setSelectedPlayerId] = useState(null);
  const [showCoinStore, setShowCoinStore] = useState(false);
  const [aiReviewEnabled, setAiReviewEnabled] = useState(false);
  const [aiReviewProcessing, setAiReviewProcessing] = useState(false);
  const [showAllSettings, setShowAllSettings] = useState(false);

  const currentUserId = localStorage.getItem("currentUserId");
  const playerName = localStorage.getItem("playerName");
  const { coinBalance, canAfford, spendCoins, isPro } = useCoinStore();
  useRoomMascots(roomDocId, roomData, currentUserId);

  const trackSettingChanged = (setting, enabled, value) => {
    if (!roomDocId) return;
    analytics.track("room_setting_changed", {
      room_id: roomDocId,
      setting,
      enabled,
      pricing: setting === "ai_review" ? "paid" : "free",
      ...(value !== undefined && { value }),
    });
  };

  React.useEffect(() => {
    const roomId = localStorage.getItem("currentRoomId");
    if (roomId) {
      // Prefer the short human-friendly code once roomData has loaded;
      // falls back to the long internal roomId before that (or for rooms
      // created before shortCode existed), so nothing ever shows blank.
      const displayCode = roomData?.shortCode || roomId;
      const url = `${window.location.origin}/join-room/${displayCode}`;
      setRoomUrl(url);
      setRoomCode(displayCode);
    }
  }, [roomData?.shortCode]);

  // The settings sheets sit above the coin store's layer; close them first so
  // the store (and its sign-in prompt) isn't opened underneath them.
  const handleBuyCoins = () => {
    showSettings(false);
    setShowAllSettings(false);
    setShowCoinStore(true);
  };

  const handleCoinStoreClose = () => {
    setShowCoinStore(false);
  };

  const handleRemovePlayerClick = (playerId, playerName) => {
    setSelectedPlayerId(playerId);
    setConfirmationModal({
      isOpen: true,
      title: "Remove Player",
      message: `Are you sure you want to remove ${playerName} from the game?`,
      onConfirm: () => removePlayer(playerId),
    });
  };

  const handleTransferHostClick = (playerId, playerName) => {
    setSelectedPlayerId(playerId);
    setConfirmationModal({
      isOpen: true,
      title: "Transfer Host Status",
      message: `Are you sure you want to make ${playerName} the new host? You will lose host privileges.`,
      onConfirm: () => transferHost(playerId),
    });
  };

  const handleDeleteRoom = () => {
    setConfirmationModal({
      isOpen: true,
      title: "Delete Room",
      message:
        "Are you sure you want to delete this room? This action cannot be undone and will remove all players.",
      confirmLabel: "Delete room",
      onConfirm: deleteRoom,
    });
  };

  // A host can't just walk out: the room would have no one to start it.
  // Leaving closes it; to keep it going they make someone else host first.
  const handleHostLeave = () => {
    const others = (roomData?.players?.length || 1) - 1;
    setConfirmationModal({
      isOpen: true,
      title: "Leave and close this room?",
      message:
        others > 0
          ? `You're the host, so leaving closes the room for the ${others} other player${others === 1 ? "" : "s"}. To keep it going, tap "Make host" on someone first, then leave.`
          : "You're the host, so leaving closes the room.",
      confirmLabel: "Close room",
      onConfirm: deleteRoom,
    });
  };

  // Turning AI checking on spends coins, so confirm the amount first.
  const confirmAiReview = (cost, onConfirm) => {
    setConfirmationModal({
      isOpen: true,
      title: `Spend ${cost} coins?`,
      message: `AI will check every answer this game instead of players voting. You have ${coinBalance} coins. Coins aren't returned if you turn it off later.`,
      confirmLabel: `Spend ${cost} coins`,
      onConfirm,
    });
  };

  const copyRoomUrl = async () => {
    const shareableText = `Join on web: ${roomUrl}\nJoin on app — use code: ${roomCode}`;
    try {
      await navigator.clipboard.writeText(shareableText);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = shareableText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const inviteFriends = async () => {
    const shareText = `Join my NameIt! game — room code ${roomCode}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "NameIt!", text: shareText, url: roomUrl });
        if (roomDocId) analytics.track("room_details_shared", { room_id: roomDocId, method: "native_share" });
      } catch {
        // Closing the share sheet rejects; nothing to do.
      }
      return;
    }
    await copyRoomUrl();
    if (roomDocId) analytics.track("room_details_shared", { room_id: roomDocId, method: "copy_link" });
  };

  const copyCodeOnly = async () => {
    try {
      await navigator.clipboard.writeText(roomCode);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = roomCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
    if (roomDocId) analytics.track("room_code_copied", { room_id: roomDocId });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-orange-50 container px-6 flex justify-center items-center">
        <div className="text-red-800 text-xl">Loading room...</div>
      </div>
    );
  }

  if (!roomData) {
    return (
      <div className="min-h-screen bg-orange-50 container px-6 flex justify-center items-center flex-col ">
        <div className="text-red-800 text-2xl">Room not found</div>
        <a
          className="mt-5 bg-red-500 p-3 rounded-md text-bold text-white "
          href={HOME_PATH}
        >
          Go Back
        </a>
      </div>
    );
  }

  // The page's pb-28 lets Start Game scroll clear of the floating chat button.
  const playerCount = roomData.players ? roomData.players.length : 0;
  const roomCategories = roomData?.categories || customCategories || CLASSIC_CATEGORIES;
  const roundTime =
    roomData?.hasCustomTimeLimit && roomData?.customTimeLimit
      ? roomData.customTimeLimit
      : customTimeLimit;
  const seatsLeft = Math.max(0, roomMaxPlayers - playerCount);
  const aiReviewCost = getAiReviewCost(roomMaxPlayers);
  const canEditSettings = isHost && gameStatus === "waiting";

  // Rematch rooms remember who was invited; show the ones who haven't joined yet.
  const pendingInvitees = (roomData?.invitedPlayers || []).filter(
    (invitee) => !roomData?.players?.some((p) => p.id === invitee.id)
  );

  const openAddSeats = (source) => {
    setShowUpgradeModal(true);
    if (roomDocId) {
      analytics.track("add_seats_opened", {
        room_id: roomDocId,
        source,
        player_count: playerCount,
        max_players: roomMaxPlayers,
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-orange-100 p-6 pb-36">
      <div className="max-w-4xl lg:max-w-5xl mx-auto">
        <header className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-red-800">NameIt!</h1>

          {/* {!isHost && gameStatus === "waiting" && (
            <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-sm rounded-full">
              Waiting for host...
            </span>
          )} */}

          <button
            onClick={() => setShowRulesModal(true)}
            className="bg-red-100 text-red-700 px-4 py-2 rounded-lg hover:bg-red-200 transition duration-300 font-semibold text-sm flex items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            Game Rules
          </button>
        </header>

        {gameStatus === "ended" ? (
          <FinalGameResults roomData={roomData} roomDocId={roomDocId} currentUserId={currentUserId} />
        ) : (
          <>
            <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-6 lg:items-start">
            <div className="min-w-0">
            {/* ROOM card — identity + sharing, restyled to sit closer to the
                mobile app's lobby card (bordered white card, label + gear up
                top, name + delete below, two pill buttons for sharing). */}
            <div className="bg-white rounded-xl shadow-md border-2 border-red-200 p-5 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wide text-gray-400">Room</span>
                {canEditSettings && (
                  <button
                    onClick={() => showSettings(true)}
                    className="p-1.5 -m-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                    title="Edit room settings"
                    aria-label="Edit room settings"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
              </div>
              <h2 className="text-lg font-semibold text-gray-900 truncate mt-1 mb-4">{roomData.roomName}</h2>

              <button
                onClick={copyCodeOnly}
                className="w-full flex items-center justify-between rounded-xl bg-gray-50 border border-gray-200 px-4 py-3 hover:bg-gray-100 transition-colors"
                title="Copy room code"
              >
                <div className="text-left">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Room code</div>
                  <div className="text-2xl font-bold tracking-[0.2em] text-gray-900 font-mono break-all">{roomCode}</div>
                </div>
                <span className={`flex items-center gap-1 text-sm font-semibold ${copiedCode ? "text-green-600" : "text-gray-500"}`}>
                  {copiedCode ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                  )}
                  {copiedCode ? "Copied" : "Copy"}
                </span>
              </button>

              {IS_VATAR && (
                <p className="mt-3 text-center text-sm text-gray-500">
                  Tell your friends this code, or open the room to everyone in public rooms.
                </p>
              )}
              <div className={`mt-3 gap-2 ${IS_VATAR ? "hidden" : "flex"}`}>
                <button
                  onClick={inviteFriends}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-semibold transition-colors ${
                    copied ? "bg-green-600 text-white" : "bg-red-600 text-white hover:bg-red-700"
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                  {copied ? "Invite link copied!" : "Invite friends"}
                </button>
                <button
                  onClick={() => {
                    setShowQRPopup(true);
                    if (roomDocId) analytics.track("room_details_shared", { room_id: roomDocId, method: "qr" });
                  }}
                  className="px-4 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors lg:hidden"
                  title="Show QR code"
                  aria-label="Show QR code"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>
                </button>
              </div>

              <div className="mt-5">
                <RoomSettingsLabel
                  canEdit={canEditSettings}
                  isGuest={!isHost && gameStatus === "waiting"}
                  onEdit={() => showSettings(true)}
                />
                <div className="mt-1.5 flex items-baseline gap-1.5 text-sm">
                  <span className="min-w-0 truncate font-medium text-gray-800 capitalize">
                    {roomCategories.slice(0, 4).join(" · ")}
                  </span>
                  {roomCategories.length > 4 && (
                    <button
                      onClick={() => setShowAllSettings(true)}
                      className="shrink-0 text-gray-500 hover:text-gray-700"
                    >
                      +{roomCategories.length - 4} more
                    </button>
                  )}
                </div>
                <div className="mt-0.5 flex items-baseline justify-between gap-3">
                  <span className="text-sm text-gray-500">{roundTime} sec rounds</span>
                  <button
                    onClick={() => setShowAllSettings(true)}
                    className="shrink-0 text-sm text-gray-500 hover:text-gray-700"
                  >
                    View all ›
                  </button>
                </div>
              </div>
            </div>

            {/* PLAYERS card */}
            <div className="bg-white rounded-xl shadow-md p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-wide text-gray-400">
                  Players · {playerCount}/{roomMaxPlayers}
                </span>
                {!IS_VATAR && isHost && gameStatus === "waiting" && seatsLeft > 1 && (
                  <button
                    onClick={() => openAddSeats("header")}
                    className="text-sm font-semibold text-red-600 hover:text-red-700"
                  >
                    + Add seats
                  </button>
                )}
              </div>

              {!IS_VATAR && isHost && gameStatus === "waiting" && seatsLeft <= 1 && (
                <button
                  onClick={() => openAddSeats("almost_full_banner")}
                  className="mb-3 w-full flex items-center justify-between gap-3 rounded-lg bg-amber-50 px-3 py-2 text-sm hover:bg-amber-100 transition-colors"
                >
                  <span className="text-amber-900">{seatsLeft === 0 ? "Room is full" : "1 seat left"}</span>
                  <span className="font-semibold text-amber-700">Add seats →</span>
                </button>
              )}

              <div className="space-y-2 mb-4">
                {roomData.players &&
                  roomData.players.map((player) => (
                    <div
                      key={player.id}
                      className={`py-2 pl-4 pr-2 min-h-[52px] rounded-lg border flex items-center justify-between gap-2 ${
                        player.isHost ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{player.name}</span>
                        {player.id === currentUserId && isPro && (
                          <span className="flex items-center gap-0.5 text-xs font-bold bg-yellow-400 text-yellow-900 px-1.5 py-0.5 rounded-full">
                            <Crown className="w-3 h-3" />
                            Pro
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1">
                        {player.isHost && (
                          <span className="text-red-700 font-bold text-xs uppercase tracking-wide pr-2">Host</span>
                        )}

                        {isHost &&
                          player.id !== currentUserId &&
                          gameStatus === "waiting" && (
                            <>
                              <button
                                onClick={() => handleTransferHostClick(player.id, player.name)}
                                className="flex items-center gap-1 min-h-[44px] px-2 rounded-lg text-xs font-semibold text-gray-600 hover:text-amber-700 hover:bg-amber-50 transition-colors"
                              >
                                <Crown className="w-3.5 h-3.5" aria-hidden="true" />
                                Make host
                              </button>

                              <button
                                onClick={() => handleRemovePlayerClick(player.id, player.name)}
                                className="flex items-center gap-1 min-h-[44px] px-2 rounded-lg text-xs font-semibold text-gray-600 hover:text-red-700 hover:bg-red-50 transition-colors"
                              >
                                <UserMinus className="w-3.5 h-3.5" aria-hidden="true" />
                                Remove
                              </button>
                            </>
                          )}
                      </div>
                    </div>
                  ))}
              </div>

              {gameStatus === "waiting" && isHost && roomData?.rematchOf && (
                <p className="mb-1 text-sm font-semibold text-green-700">{REPLAY_COPY.lobbySubtitle}</p>
              )}
              {gameStatus === "waiting" && pendingInvitees.length > 0 && (
                <p className="mb-4 text-sm text-gray-600">{waitingForPlayers(pendingInvitees.map((p) => p.name))}</p>
              )}

              {gameStatus === "waiting" && (
                <LobbyMascot
                  className="mb-3"
                  message={
                    playerCount < 2
                      ? "You need at least two players to start the game."
                      : isHost
                        ? "Everyone's here? Start when you're ready!"
                        : "Waiting for the host to start the game…"
                  }
                />
              )}

              <div className="lg:fixed lg:inset-x-0 lg:bottom-0 lg:z-30 lg:px-6 lg:pointer-events-none">
              <div className="lg:max-w-5xl lg:mx-auto">
              <div className="lg:w-[calc(100%-364px)] lg:pointer-events-auto lg:bg-white lg:border lg:border-b-0 lg:border-orange-200 lg:rounded-t-xl lg:p-3.5 lg:shadow-[0_-4px_14px_rgba(0,0,0,0.06)]">
              {isHost ? (
                <div className="flex gap-2">
                  {gameStatus === "waiting" && (
                    <>
                      <button
                        onClick={handleHostLeave}
                        className="px-4 bg-white border border-gray-300 text-gray-700 font-semibold py-3 rounded-xl hover:bg-gray-50 transition duration-300"
                      >
                        Leave
                      </button>
                      {/* Always visible so the host knows where it will be;
                          disabled until there's someone to play with. */}
                      <button
                        onClick={() => startGame()}
                        disabled={playerCount < 2 || startingGame}
                        className="flex-1 bg-red-600 text-white font-bold py-3 rounded-xl hover:bg-red-700 transition duration-300 shadow-sm disabled:bg-gray-300 disabled:text-gray-700 disabled:shadow-none disabled:cursor-not-allowed"
                      >
                        Start game
                      </button>
                    </>
                  )}
                  {(gameStatus === "playing" || gameStatus === "starting") && (
                    <button
                      onClick={() =>
                        setConfirmationModal({
                          isOpen: true,
                          title: "End the game for everyone?",
                          message: "The current round finishes, but no new rounds start.",
                          confirmLabel: "End game",
                          onConfirm: endGame,
                        })
                      }
                      className="flex-1 bg-gray-600 text-white font-semibold py-3 rounded-xl hover:bg-gray-700 transition duration-300 shadow-sm"
                    >
                      End Game
                    </button>
                  )}
                </div>
              ) : (
                <button
                  onClick={leaveRoom}
                  className="w-full bg-gray-600 text-white font-semibold py-3 rounded-xl hover:bg-gray-700 transition duration-300 shadow-sm"
                >
                  Leave Room
                </button>
              )}
              </div>
              </div>
              </div>
            </div>
            </div>

            {!IS_VATAR && (
              <aside
                aria-label="Share this room"
                className="hidden lg:flex lg:sticky lg:top-6 flex-col items-center gap-3 bg-white rounded-xl shadow-md border-2 border-red-200 p-5 text-center"
              >
                <h2 className="text-base font-bold text-gray-900">Join on your phone</h2>
                <div className="rounded-xl border border-gray-200 p-3 bg-white">
                  <BrandedQRCode value={roomUrl} size={184} />
                </div>
                <p className="text-sm leading-snug text-gray-600">
                  Point your phone camera here to join, or enter code{" "}
                  <strong className="font-mono tracking-widest text-gray-900">{roomCode}</strong>
                </p>
                <button
                  onClick={async () => {
                    await copyRoomUrl();
                    if (roomDocId) analytics.track("room_details_shared", { room_id: roomDocId, method: "copy_link" });
                  }}
                  className={`w-full py-2.5 rounded-lg border font-semibold text-sm transition-colors ${
                    copied ? "border-green-600 text-green-700 bg-green-50" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {copied ? "Link copied!" : "Copy invite link"}
                </button>
                <div className="w-full border-t border-gray-100 pt-3">
                  <p className="text-3xl font-black text-red-700">
                    {playerCount}
                    <span className="ml-1.5 text-base font-semibold text-gray-600">
                      of {roomMaxPlayers} players in the room
                    </span>
                  </p>
                  {roomMaxPlayers <= 10 && (
                    <div className="mt-2 flex justify-center gap-1.5" aria-hidden="true">
                      {Array.from({ length: roomMaxPlayers }, (_, i) => (
                        <span
                          key={i}
                          className={`w-3.5 h-3.5 rounded-full ${i < playerCount ? "bg-red-600" : "border-2 border-dashed border-gray-300"}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </aside>
            )}
            </div>
              {showAllSettings && (
                <div
                  className="fixed inset-0 bg-black bg-opacity-40 z-[60] flex items-end sm:items-center justify-center p-4"
                  onClick={() => setShowAllSettings(false)}
                >
                  <div
                    className="bg-white rounded-xl shadow-xl max-w-sm w-full max-h-[85vh] overflow-y-auto p-6"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-800">Room settings</h3>
                      <button
                        onClick={() => setShowAllSettings(false)}
                        className="p-2 -mr-2 text-gray-400 hover:text-gray-600 rounded-full"
                        aria-label="Close"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <dl className="divide-y divide-gray-100 text-sm">
                      {[
                        ["Categories", roomCategories.map((c) => c.replace(/\b\w/g, (ch) => ch.toUpperCase())).join(", ")],
                        ["Round time", `${roundTime} seconds`],
                        ["Letters", roomData?.hasLetterPicker ? "Players take turns choosing" : "Random each round"],
                        ["Special rounds", roomData?.enableSpecialRounds ? "On" : "Off"],
                        ["Answer checking", roomData?.reviewMode === "ai" || roomData?.aiReviewEnabled ? "AI checks answers" : "Players vote"],
                        ["Players", `${playerCount} of ${roomMaxPlayers} seats`],
                      ].map(([label, value]) => (
                        <div key={label} className="flex justify-between gap-4 py-2.5">
                          <dt className="text-gray-500 shrink-0">{label}</dt>
                          <dd className="text-gray-800 font-medium text-right break-words min-w-0">{value}</dd>
                        </div>
                      ))}
                    </dl>
                    {canEditSettings && (
                      <button
                        onClick={() => {
                          setShowAllSettings(false);
                          showSettings(true);
                        }}
                        className="mt-5 w-full py-2.5 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        Change settings
                      </button>
                    )}
                  </div>
                </div>
              )}
              {settings && (
                <div className="fixed inset-0 bg-black bg-opacity-40 z-[60] flex items-center justify-center p-4">
                  <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
                    {isHost && gameStatus === "waiting" && (
                      <div className="p-6">
                        <div className="flex justify-between items-center border-b border-gray-100 pb-4 mb-6">
                          <h3 className="text-2xl font-bold text-gray-800">
                            Game settings
                          </h3>
                          <button
                            onClick={() => showSettings(false)}
                            className="text-gray-500 hover:text-gray-700 transition-colors p-2 -mr-2 rounded-full hover:bg-gray-100"
                            title="Close settings"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-6 w-6"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>

                        <div className="mb-8">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h4 className="text-base font-semibold text-gray-800">
                                Categories
                              </h4>
                              <p className="text-xs text-gray-500">
                                What players answer each round
                              </p>
                            </div>
                            <button
                              onClick={() => setShowCategoryManager(true)}
                              className="text-sm px-4 py-1.5 rounded-lg border border-red-600 text-red-600 hover:bg-red-50 font-semibold"
                            >
                              Edit
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {roomCategories.map((category, index) => (
                              <div
                                key={index}
                                className="px-3 py-2 text-sm rounded-lg text-center font-medium capitalize bg-gray-50 border border-gray-200 truncate"
                              >
                                {category}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="mb-8">
                          <h4 className="text-base font-semibold text-gray-800">
                            Round time
                          </h4>
                          <p className="text-xs text-gray-500 mb-3">
                            How long players have to fill in answers.
                          </p>
                          <div className="grid grid-cols-4 gap-1 p-1 bg-gray-100 rounded-xl">
                            {TIMER_OPTIONS.map(({ seconds, label }) => {
                              const selected = seconds === roundTime;
                              return (
                                <button
                                  key={seconds}
                                  type="button"
                                  aria-pressed={selected}
                                  onClick={() => {
                                    if (selected) return;
                                    setCustomTimeLimit(seconds);
                                    updateCustomTimeLimitValue(seconds);
                                    trackSettingChanged("custom_timer", true, seconds);
                                  }}
                                  className={`py-2 rounded-lg transition-colors ${
                                    selected
                                      ? "bg-white shadow-sm text-red-600"
                                      : "text-gray-600 hover:text-gray-800"
                                  }`}
                                >
                                  <div className="text-sm font-bold">{seconds}s</div>
                                  <div className="text-[11px]">{label}</div>
                                </button>
                              );
                            })}
                          </div>
                          {!TIMER_OPTIONS.some((o) => o.seconds === roundTime) && (
                            <p className="text-xs text-gray-500 mt-2">
                              This room is set to {formatTimeDisplay(roundTime)}. Pick a time above to change it.
                            </p>
                          )}
                        </div>

                        <div className="mb-8 space-y-3">
                          <SettingToggle
                            title="Players choose the letter"
                            description="Take turns picking the starting letter each round. When off, the letter is random."
                            checked={!!(hasLetterPicker || roomData?.hasLetterPicker)}
                            onClick={() => {
                              const newState = !(hasLetterPicker || roomData?.hasLetterPicker);
                              setHasLetterPicker(newState);
                              trackSettingChanged("letter_picker", newState);
                              if (roomDocId) {
                                updateDoc(doc(db, "rooms", roomDocId), {
                                  hasLetterPicker: newState,
                                });
                              }
                            }}
                          />
                          <SettingToggle
                            title="Special rounds"
                            description="Some rounds get a twist, like double points or a penalty for blank answers."
                            checked={enableSpecialRounds}
                            onClick={async () => {
                              const next = !enableSpecialRounds;
                              setEnableSpecialRounds(next);
                              trackSettingChanged("special_rounds", next);
                              if (roomDocId) {
                                try {
                                  await updateDoc(doc(db, "rooms", roomDocId), {
                                    enableSpecialRounds: next,
                                  });
                                } catch (err) {
                                  console.error("Failed to update special rounds:", err);
                                  setEnableSpecialRounds(!next);
                                }
                              }
                            }}
                          />
                        </div>

                        <div className="border-t border-gray-100 pt-6">
                          <SettingToggle
                            title="Let AI check answers"
                            badge={
                              <span className="text-xs font-semibold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                                {aiReviewCost} coins
                              </span>
                            }
                            description={
                              aiReviewProcessing
                                ? "Turning on..."
                                : aiReviewEnabled
                                ? "On — AI will check every answer this game."
                                : `Skip voting. AI decides which answers count, so there are no arguments. ${aiReviewCost} coins for this game.`
                            }
                            checked={aiReviewEnabled}
                            disabled={aiReviewProcessing || (!aiReviewEnabled && !canAfford(aiReviewCost))}
                            onClick={async () => {
                              if (aiReviewProcessing) return;
                              if (aiReviewEnabled) {
                                setAiReviewEnabled(false);
                                trackSettingChanged("ai_review", false);
                                if (roomDocId) {
                                  try {
                                    await updateDoc(doc(db, "rooms", roomDocId), { reviewMode: "peer", aiReviewEnabled: false });
                                  } catch (err) {
                                    console.error("Failed to disable AI review:", err);
                                    setAiReviewEnabled(true);
                                  }
                                }
                                return;
                              }
                              if (!canAfford(aiReviewCost)) return;
                              confirmAiReview(aiReviewCost, async () => {
                                setAiReviewProcessing(true);
                                const result = await spendCoins(aiReviewCost, "ai_review", roomDocId);
                                setAiReviewProcessing(false);
                                if (!result.success) return;
                                setAiReviewEnabled(true);
                                trackSettingChanged("ai_review", true);
                                if (roomDocId) {
                                  try {
                                    await updateDoc(doc(db, "rooms", roomDocId), { reviewMode: "ai", aiReviewEnabled: true });
                                  } catch (err) {
                                    console.error("Failed to enable AI review:", err);
                                    setAiReviewEnabled(false);
                                  }
                                }
                              });
                            }}
                          />
                          {!aiReviewEnabled && (
                            <p className="text-xs text-gray-500 mt-2 px-1">
                              You have {coinBalance} coins
                              {!canAfford(aiReviewCost) && (
                                <>
                                  {" · "}
                                  <button onClick={handleBuyCoins} className="font-semibold text-red-600 hover:underline">
                                    Get coins
                                  </button>
                                </>
                              )}
                            </p>
                          )}
                        </div>

                        <div className="border-t border-gray-100 mt-6 pt-4 text-center">
                          <button
                            onClick={() => {
                              showSettings(false);
                              handleDeleteRoom();
                            }}
                            className="text-sm font-semibold text-red-600 hover:underline"
                          >
                            Delete room
                          </button>
                        </div>

                      </div>
                    )}
                  </div>
                </div>
              )}
          </>
        )}
      </div>

      <GameRulesModal
        isOpen={showRulesModal}
        onClose={() => setShowRulesModal(false)}
      />

      {showCategoryManager && (
        <CategoryManager
          currentCategories={roomCategories}
          onCategoriesChange={handleCategoriesChange}
          onBuyCoins={handleBuyCoins}
          roomId={roomDocId}
          onClose={() => setShowCategoryManager(false)}
        />
      )}

      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() =>
          setConfirmationModal({ ...confirmationModal, isOpen: false })
        }
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmLabel={confirmationModal.confirmLabel}
      />

      <ChatContainer
        roomData={roomData}
        roomDocId={roomDocId}
        currentUserId={currentUserId}
        playerName={playerName}
        position="bottom-right"
        showInPhases={["lobby"]}
      />

      {showQRPopup && (
        <QRCodePopup roomUrl={roomUrl} onClose={() => setShowQRPopup(false)} />
      )}

      <RoomUpgrade
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        roomId={roomDocId}
        currentMaxPlayers={roomMaxPlayers}
        onUpgrade={handleRoomUpgrade}
      />

      <CoinStore isOpen={showCoinStore} onClose={handleCoinStoreClose} />

      {hostingLimit && (
        <HostingLimitModal
          coinBalance={coinBalance}
          cost={hostingLimit.cost || ROOM_LIMIT_COST}
          nextAvailableAt={hostingLimit.nextAvailableAt}
          serverNow={hostingLimit.serverNow}
          busy={startingGame}
          error={hostingLimit.error}
          onHostNow={() => startGame({ payWithCoins: true })}
          onGetCoins={() => {
            setHostingLimit(null);
            setShowCoinStore(true);
          }}
          onClose={() => setHostingLimit(null)}
        />
      )}
    </div>
  );
};

export default CreateRoom;
